import React, { useState, useRef, useEffect } from 'react';
import './MobileAudioControls.css';

const MobileAudioControls = ({
  audioRef,
  isPlaying,
  currentTime,
  duration,
  skipDuration = 5,
  onPlay,
  onPause,
  onSeek,
  showControls = true,
  isLoading = false,
  rightButtons
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [internalIsPlaying, setInternalIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const progressRef = useRef(null);
  const speedMenuRef = useRef(null);

  // Update internal playing state when audio element changes
  useEffect(() => {
    if (audioRef?.current) {
      const audio = audioRef.current;
      const updatePlayingState = () => {
        const playing = !audio.paused;
        console.log('[MobileAudioControls] Audio state changed:', playing ? 'playing' : 'paused');
        setInternalIsPlaying(playing);
      };

      audio.addEventListener('play', updatePlayingState);
      audio.addEventListener('pause', updatePlayingState);
      audio.addEventListener('ended', updatePlayingState);
      audio.addEventListener('loadeddata', updatePlayingState);

      // Initial state
      updatePlayingState();

      return () => {
        audio.removeEventListener('play', updatePlayingState);
        audio.removeEventListener('pause', updatePlayingState);
        audio.removeEventListener('ended', updatePlayingState);
        audio.removeEventListener('loadeddata', updatePlayingState);
      };
    }
  }, [audioRef, audioRef?.current]);

  // Add/remove body class for mobile controls
  useEffect(() => {
    if (showControls && window.innerWidth <= 768) {
      document.body.classList.add('mobile-audio-active');
    } else {
      document.body.classList.remove('mobile-audio-active');
    }

    return () => {
      document.body.classList.remove('mobile-audio-active');
    };
  }, [showControls]);

  // Handle playback speed change
  const handleSpeedChange = (rate) => {
    if (audioRef?.current) {
      audioRef.current.playbackRate = rate;
      setPlaybackRate(rate);
      setShowSpeedMenu(false);
    }
  };

  // Close speed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(event.target)) {
        setShowSpeedMenu(false);
      }
    };

    if (showSpeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSpeedMenu]);

  // Skip forward/backward by specified seconds
  const skipTime = (seconds) => {
    if (audioRef?.current && duration > 0) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
      if (onSeek) onSeek(newTime);
    }
  };

  // Toggle play/pause
  const togglePlayPause = async () => {
    if (audioRef?.current) {
      try {
        const audio = audioRef.current;
        console.log('[Mobile Audio] Toggle play/pause:', {
          paused: audio.paused,
          src: audio.src,
          readyState: audio.readyState,
          networkState: audio.networkState
        });
        // Check audio element's paused state directly, not React state
        if (!audio.paused) {
          audio.pause();
          // State will be updated by event listener
        } else {
          // Try to load if not loaded
          if (audio.readyState === 0) {
            console.log('[Mobile Audio] Loading audio...');
            audio.load();
          }
          await audio.play();
          console.log('[Mobile Audio] Play successful');
          // State will be updated by event listener
        }
      } catch (error) {
        console.error('Mobile controls play/pause error:', error);
        console.error('Mobile audio state:', {
          src: audioRef.current?.src,
          error: audioRef.current?.error
        });
      }
    } else {
      console.error('[Mobile Audio] No audioRef available');
    }
  };

  // Handle progress bar click/touch
  const handleProgressInteraction = (clientX) => {
    if (!progressRef.current || duration <= 0) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = percent * duration;

    if (audioRef?.current) {
      audioRef.current.currentTime = newTime;
      if (onSeek) onSeek(newTime);
    }
  };

  const handleProgressClick = (e) => {
    e.preventDefault();
    handleProgressInteraction(e.clientX);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const touch = e.touches[0];
    handleProgressInteraction(touch.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    handleProgressInteraction(touch.clientX);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Format time display
  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage with defensive checks to prevent negative values
  const progressPercent = duration > 0 && currentTime >= 0
    ? Math.max(0, Math.min(100, (currentTime / duration) * 100))
    : 0;

  if (!showControls) {
    return null;
  }

  return (
    <div className="mobile-audio-controls">
      <div className="mobile-progress-section">
        <div
          ref={progressRef}
          className="mobile-progress-bar"
          onClick={handleProgressClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="mobile-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
          <div
            className="mobile-progress-handle"
            style={{ left: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="mobile-controls-row">
        <div className="mobile-controls-main-row">
          {duration > 0 && (
            <div className="mobile-time-display">
              <span className="mobile-current-time">{formatTime(currentTime)}</span>
              <span className="mobile-time-separator">/</span>
              <span className="mobile-total-time">{formatTime(duration)}</span>
            </div>
          )}

          <div className="mobile-playback-buttons">
            <button
              className="mobile-control-btn mobile-skip-btn"
              onClick={() => skipTime(-skipDuration)}
              title={`Skip back ${skipDuration}s`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z"/>
              </svg>
              <span className="mobile-skip-text">{skipDuration}s</span>
            </button>

            <button
              className="mobile-control-btn mobile-play-pause-btn"
              onClick={togglePlayPause}
              title={isLoading ? 'Loading...' : (internalIsPlaying ? 'Pause' : 'Play')}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="circular-spinner"></div>
              ) : internalIsPlaying ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6zm8 0h4v16h-4z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            <button
              className="mobile-control-btn mobile-skip-btn"
              onClick={() => skipTime(skipDuration)}
              title={`Skip forward ${skipDuration}s`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 18l8.5-6L4 6v12zM13 6v12l8.5-6L13 6z"/>
              </svg>
              <span className="mobile-skip-text">{skipDuration}s</span>
            </button>
          </div>

          <div className="mobile-playback-side-controls">
            <div className="mobile-speed-control" ref={speedMenuRef}>
              <button
                className="mobile-control-btn mobile-speed-btn"
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                title="Playback speed"
              >
                <span className="mobile-speed-text">{playbackRate}x</span>
              </button>
              {showSpeedMenu && (
                <div className="mobile-speed-menu">
                  {[0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0].map((rate) => (
                    <button
                      key={rate}
                      className={`mobile-speed-option ${rate === playbackRate ? 'active' : ''}`}
                      onClick={() => handleSpeedChange(rate)}
                    >
                      {rate}x
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {rightButtons && (
          <div className="mobile-controls-edit-row">
            <div className="mobile-edit-buttons">{rightButtons}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileAudioControls;